import React, { useState, useEffect, useMemo } from "react";
import Page from "../shared/components/PageComponent";
import Layout from "../shared/components/LayoutComponent";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SearchTextField } from "../lib";
import { useTranslation } from "react-i18next";
import apiRequest from "../../functions/api";
import { useMessage } from "../../context/message-context";

// Icons
import { FilterList } from "@mui/icons-material";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

const program = "KMPAPI";

function DeviceSuche(props) {
  const { accessToken, client } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showErrorMessage } = useMessage();

  // Local Storage
  const storedFilterObject = JSON.parse(localStorage.getItem("DeviceSucheFilter"));
  const [deviceFilter, setDeviceFilter] = useState(storedFilterObject?.deviceFilter ? storedFilterObject?.deviceFilter : []);
  const [einheitFilter, setEinheitFilter] = useState(storedFilterObject?.einheitFilter ? storedFilterObject?.einheitFilter : []);

  const [filterDialog, setFilterDialog] = useState(false);
  const [suche, setSuche] = useState("");
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const einheitOptions = useMemo(() => {
    // Herrausfiltern der einzelnen Optionen
    const tmpfilter = devices
      ?.filter((value, index, array) => array.findIndex((element) => element.EINHEIT === value.EINHEIT) === index)
      .map((mapElement) => {
        return {
          value: mapElement.EINHEIT,
          label: mapElement.EINHEIT,
        };
      })
      ?.filter((item) => item !== null);
    return tmpfilter ? tmpfilter : [];
  }, [devices]);

  const deviceOptions = useMemo(() => {
    // Herrausfiltern der einzelnen Optionen
    const tmpfilter = devices
      ?.filter((value, index, array) => array.findIndex((element) => element.BEZ === value.BEZ) === index)
      .map((mapElement) => {
        return {
          value: mapElement.BEZ,
          label: mapElement.BEZ,
        };
      })
      ?.filter((item) => item !== null);
    return tmpfilter ? tmpfilter : [];
  }, [devices]);

  // ----------------------------------------------------
  // -----------Allgemeine funktionen--------------------
  // ----------------------------------------------------
  useEffect(() => {
    setIsLoading(true);
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      statement: "DEVICES_HOLEN",
    };

    apiRequest("POST", "daten", payload, headers, datenCb, errorCb);
  }, []);

  useEffect(() => {
    const result = devices?.filter((item) => {
      const tmpDevices = deviceFilter?.length !== 0 ? deviceFilter?.some((searchItem) => [item.BEZ].includes(searchItem.label)) : true;

      const tmpEinheit = einheitFilter?.length !== 0 ? einheitFilter?.some((searchItem) => searchItem.label === item.EINHEIT) : true;

      const tmpSuche = suche?.trim() !== "" ? [item.BEZ, item.EINHEIT].some((field) => field?.toLowerCase().includes(suche.toLowerCase())) : true;
      return tmpSuche && tmpDevices && tmpEinheit;
    });

    setFilteredDevices(result);
  }, [devices, suche, deviceFilter, einheitFilter]);

  useEffect(() => {
    const filterObject = {
      deviceFilter: deviceFilter,
      einheitFilter: einheitFilter,
    };
    localStorage.setItem("DeviceSucheFilter", JSON.stringify(filterObject));
  }, [deviceFilter, einheitFilter]);

  const datenCb = (response) => {
    setIsLoading(false);
    if (response.status === 200) {
      setDevices(response.data.device);
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const errorCb = () => {
    showErrorMessage(t("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const rowClick = (id) => {
    navigate(`/KMPAPI/device/${id}`);
  };

  const resetFilter = () => {
    setSuche("");
    setDeviceFilter([]);
    setEinheitFilter([]);
  };

  // ----------------------------------------------------
  // -------------Handy funktionen-----------------------
  // ----------------------------------------------------
  const MobileMain = () => {
    return (
      <>
        <Box sx={{ paddingTop: 2 }}>
          {filteredDevices && (
            <Box>
              <TableContainer sx={{ maxHeight: window.innerHeight - 250 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography>Gerät</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>Einheit</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading && (
                      <TableRow key={"loading"}>
                        <TableCell align="center" colSpan={3}>
                          <CircularProgress size={24}></CircularProgress>
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading &&
                      filteredDevices?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
                        return (
                          <TableRow onClick={() => rowClick(item.ID)} key={item.ID}>
                            <TableCell>{item.BEZ}</TableCell>
                            <TableCell>
                              <Typography>{item.EINHEIT}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredDevices?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage=""
              />
            </Box>
          )}

          {!filteredDevices && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 240,
                color: "text.secondary",
                textAlign: "center",
                px: 2,
              }}
            >
              <InboxOutlinedIcon
                sx={{
                  fontSize: 64,
                  color: "action.disabled",
                  mb: 2,
                }}
              />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t("Keine Kabelverbindung gefunden")}
              </Typography>

              <Typography variant="body2" sx={{ maxWidth: 320 }}>
                {t("Es wurden noch keine Kabel angelegt. Legen Sie zunächst ein Kabel an, damit hier Ergebnisse angezeigt werden können.")}
              </Typography>
            </Box>
          )}
        </Box>
      </>
    );
  };

  // ----------------------------------------------------
  // -------------Desktop funktionen---------------------
  // ----------------------------------------------------
  const DesktopMain = () => {
    return <>Desktop nicht fertig</>;
  };

  return (
    <Page>
      <Layout>
        {/* Desktop */}
        <Box sx={{ display: { xs: "none", sm: "none", md: "block" }, width: "100%" }}>
          {/* Filter */}
          <Grid container justifyContent="center" spacing={1} style={{ paddingTop: 20 }}>
            <Grid item xs={12} md={1}>
              <Tooltip title={t("Zurück zur Hauptauswahl")}>
                <IconButton color="primary" onClick={() => navigate("/KMPAPI/")}>
                  <ReplyAllIcon />
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs={12} md={4}>
              <SearchTextField
                size="small"
                fullWidth
                label={t("Suche")}
                id="filter"
                variant="outlined"
                value={suche}
                inputProps={{
                  autoCapitalize: "off",
                  autoCorrect: "off",
                  spellCheck: "false",
                  autoComplete: "off",
                }}
                onChange={(e) => setSuche(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                multiple
                onChange={(event, newValue) => {
                  setDeviceFilter(newValue);
                }}
                value={deviceFilter}
                size="small"
                id="tags-outlined"
                options={deviceOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => <TextField {...params} label="Nach Gerät filtern" placeholder="Gerät Filter" />}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Autocomplete
                multiple
                onChange={(event, newValue) => {
                  setEinheitFilter(newValue);
                }}
                value={einheitFilter}
                size="small"
                id="tags-outlined"
                options={einheitOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => <TextField {...params} label="Nach Einheit (Rack) filtern" placeholder="Einheit Filter" />}
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <IconButton color="error" onClick={() => resetFilter()}>
                <FilterListOffIcon />
              </IconButton>
            </Grid>
          </Grid>

          <MobileMain />
        </Box>

        {/* Mobile */}
        <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
          {/* Filter */}
          <Box sx={{ paddingTop: 2 }} display="flex" alignItems={"center"}>
            <Tooltip title={t("Zurück zur Hauptauswahl")}>
              <IconButton color="primary" onClick={() => navigate("/KMPAPI/")}>
                <ReplyAllIcon />
              </IconButton>
            </Tooltip>
            <SearchTextField
              size="small"
              fullWidth
              label={t("Suche")}
              id="filter"
              variant="outlined"
              sx={{ marginLeft: "16px" }}
              value={suche}
              inputProps={{
                autoCapitalize: "off",
                autoCorrect: "off",
                spellCheck: "false",
                autoComplete: "off",
              }}
              onChange={(e) => setSuche(e.target.value)}
            />

            <Box pr={1}>
              <Tooltip title="Filter">
                <IconButton color="primary" onClick={() => setFilterDialog(true)}>
                  <FilterList />
                </IconButton>
              </Tooltip>
            </Box>
            <Box pr={1}>
              <Tooltip title="Filter entfernen">
                <IconButton color="error" onClick={() => resetFilter()}>
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Dialog open={filterDialog} onClose={() => setFilterDialog(false)} maxWidth="md" fullWidth>
              <DialogTitle>Filter</DialogTitle>
              <DialogContent>
                <Grid container justifyContent="center" spacing={1} style={{ paddingTop: 20 }}>
                  <Grid item xs={12} md={5}></Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => {
                        setDeviceFilter(newValue);
                      }}
                      value={deviceFilter}
                      size="small"
                      id="tags-outlined"
                      options={deviceOptions}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Nach Gerät filtern" placeholder="Gerät Filter" />}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => {
                        setEinheitFilter(newValue);
                      }}
                      value={einheitFilter}
                      size="small"
                      id="tags-outlined"
                      options={einheitOptions}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Nach Einheit (Rack) filtern" placeholder="Einheit Filter" />}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button style={{ marginRight: "10px" }} color="primary" onClick={() => setFilterDialog(false)}>
                  {t("Fertig")}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          <MobileMain />
        </Box>
      </Layout>
    </Page>
  );
}

export default DeviceSuche;
